import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [liveMatches, setLiveMatches] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('live'); // Default to live matches
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);

  // Fetch all match data
  const fetchAllMatchData = async () => {
    setIsLoading(true);
    
    try {
    // Fetch upcoming matches
      const upcomingResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/api/match-setup/upcoming`);
      if (upcomingResponse.ok) {
        const data = await upcomingResponse.json();
        if (data.success) {
          setUpcomingMatches(data.data);
        }
      }

    // Fetch all matches
      const allResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/api/match-setup`);
      if (allResponse.ok) {
        const data = await allResponse.json();
        if (data.success) {
          const nonLiveMatches = data.data.filter(match => match.status !== 'live');
          setAllMatches(nonLiveMatches);
        }
      }

  // Fetch live matches
      const liveResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/api/match-setup/live`);
      if (liveResponse.ok) {
        const data = await liveResponse.json();
        if (data.success) {
          setLiveMatches(data.data);
        }
        }
      } catch (err) {
      console.error('Error fetching match data:', err);
      toast.error('Failed to refresh match data');
      } finally {
      setIsLoading(false);
        setLoading(false);
      }
    };

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    // Fetch all match data initially
    fetchAllMatchData();

    // Add event listener for when page regains focus
    const handleFocus = () => {
      console.log('Dashboard page regained focus, refreshing match data');
      fetchAllMatchData();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleAddMatch = () => {
    navigate('/admin/add-match');
  };

  const handleSetUpcoming = (match) => {
    navigate('/admin/match-setup', { state: { match } });
  };

  const handleDeleteMatch = async (matchId) => {
    // Show confirmation dialog using toastify
    if (!window.confirm('Are you sure you want to delete this match? This action cannot be undone.')) {
      return;
    }
    
    try {
      console.log(`Attempting to delete match with ID: ${matchId}`);
      
      // Ensure matchId is properly formatted
      if (!matchId) {
        console.error('Match ID is undefined or null');
        toast.error('Cannot delete match: Invalid ID');
        return;
      }

      // Show loading toast
      const loadingToastId = toast.loading("Deleting match...");

      // Make sure there are no trailing ellipses in the URL
      // This can happen if the console log truncates the ID with "..."
      const cleanId = matchId.toString().replace('…', '');
      
      // Use the full URL to ensure proper routing
      const url = `${process.env.REACT_APP_BASE_URL}/api/match-setup/${cleanId}`;
      console.log('Making DELETE request to:', url);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      // Log the complete response details for debugging
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      // Handle JSON or non-JSON responses
      let responseData;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          responseData = await response.json();
          console.log('JSON response:', responseData);
        } else {
          const textData = await response.text();
          console.log('Text response:', textData);
          // For non-JSON responses, create a simulated response object
          responseData = { 
            success: response.ok,
            message: response.ok ? 'Match deleted successfully' : 'Failed to delete match'
          };
        }
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        responseData = { success: false, message: 'Error processing server response' };
      }
      
      // Update toast with result
      toast.dismiss(loadingToastId);
      
      if (!response.ok) {
        toast.error(`Failed to delete match: ${responseData.message || `Server returned ${response.status}`}`);
        throw new Error(responseData.message || `Server returned ${response.status}`);
      }
      
      // On successful delete, update the UI by removing the match from state
      setLiveMatches(prev => prev.filter(match => match._id !== matchId));
      setUpcomingMatches(prev => prev.filter(match => match._id !== matchId));
      setAllMatches(prev => prev.filter(match => match._id !== matchId));
      
      toast.success('Match deleted successfully');
    } catch (error) {
      console.error('Error deleting match:', error);
      toast.error(`Failed to delete match: ${error.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading live matches...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>Admin Dashboard</h1>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>

      <nav className="dashboard-nav">
        <button 
          className={`nav-button ${activeSection === 'live' ? 'active' : ''}`}
          onClick={() => setActiveSection('live')}
        >
          <span className="nav-icon">⚽</span>
          Live Matches
        </button>
        <button 
          className={`nav-button ${activeSection === 'upcoming' ? 'active' : ''}`}
          onClick={() => setActiveSection('upcoming')}
        >
          <span className="nav-icon">📅</span>
          Upcoming
        </button>
        <button 
          className={`nav-button ${activeSection === 'all' ? 'active' : ''}`}
          onClick={() => setActiveSection('all')}
        >
          <span className="nav-icon">📋</span>
          All Matches
        </button>
        <button 
          className="nav-button add-match"
          onClick={handleAddMatch}
        >
          <span className="nav-icon">➕</span>
          Add Match
        </button>
      </nav>

      <main className="dashboard-content">
        {activeSection === 'live' && (
          <section className="live-matches">
            <div className="section-header">
              <h2>Live Matches</h2>
            </div>
            {liveMatches.length === 0 ? (
              <p className="no-matches">No live matches at the moment</p>
            ) : (
              <div className="matches-grid">
                {liveMatches.map(match => (
                  <div key={match._id} className="match-card live">
                    <div className="match-header">
                      <h3>{match.team1} vs {match.team2}</h3>
                      <span className="match-status live">LIVE</span>
                    </div>
                    <div className="match-details">
                      <p>Date: {match.matchDate}</p>
                      <p>Time: {match.matchTime}</p>
                    </div>
                    <div className="match-actions">
                      <button 
                        className="action-button edit"
                        onClick={() => navigate('/admin/match-setup', { state: { match } })}
                      >
                        Edit
                      </button>
                      <button 
                        className="action-button delete"
                        onClick={() => handleDeleteMatch(match._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {activeSection === 'upcoming' && (
          <section className="matches-section">
            <div className="section-header">
              <h2>Upcoming Matches</h2>
            </div>
            
            <div className="matches-grid">
              {upcomingMatches.length === 0 ? (
                <p className="no-matches">No upcoming matches</p>
              ) : (
                upcomingMatches.map(match => {
                  console.log('Upcoming match:', match); // Debug log
                  return (
                    <div key={match._id} className="match-card upcoming">
                      <div className="match-teams">
                        <div className="team">
                          <span className="team-name">{match.team1}</span>
                        </div>
                        <div className="vs">VS</div>
                        <div className="team">
                          <span className="team-name">{match.team2}</span>
                        </div>
                      </div>
                      <div className="match-details">
                        <span className="match-date">{match.matchDate}</span>
                        <span className="match-time">{match.matchTime}</span>
                      </div>
                      <div className="match-actions">
                        <button 
                          className="action-button edit"
                          onClick={() => navigate('/admin/match-setup', { state: { match } })}
                        >
                          Edit
                        </button>
                        <button 
                          className="action-button delete"
                          onClick={() => handleDeleteMatch(match._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        )}

        {activeSection === 'all' && (
          <section className="matches-section">
            <div className="section-header">
              <h2>All Matches</h2>
            </div>
            
            <div className="matches-grid">
              {allMatches.length === 0 ? (
                <p className="no-matches">No matches found</p>
              ) : (
                allMatches.map(match => {
                  console.log('All match:', match); // Debug log
                  return (
                    <div key={match._id} className="match-card all">
                      <div className="match-teams">
                        <div className="team">
                          <span className="team-name">{match.team1}</span>
                        </div>
                        <div className="vs">VS</div>
                        <div className="team">
                          <span className="team-name">{match.team2}</span>
                        </div>
                      </div>
                      <div className="match-details">
                        <span className="match-date">{match.matchDate}</span>
                        <span className="match-time">{match.matchTime}</span>
                        <span className={`match-status ${match.status}`}>{match.status}</span>
                      </div>
                      <div className="match-actions">
                        {match.status !== 'upcoming' && (
                          <button 
                            className="action-button set-upcoming"
                            onClick={() => handleSetUpcoming(match)}
                          >
                            Set as Upcoming
                          </button>
                        )}
                        <button 
                          className="action-button edit"
                          onClick={() => navigate('/admin/match-setup', { state: { match } })}
                        >
                          Edit
                        </button>
                        <button 
                          className="action-button delete"
                          onClick={() => handleDeleteMatch(match._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

export default AdminDashboard; 